// @vendors
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

// @components
import GoogleSignInButton from './GoogleSignInButton';
import PendingVerificationModal from './PendingVerificationModal';

// @context
import { useAuth, UserInfo } from '../context/AuthContext';
import { useOnboarding } from '../context/OnboardingContext';
import { useTheme } from '../context/ThemeContext';

// @services
import UserService, { BasicUserCreate } from '../services/user.service';
import FirebaseAuthService from '../services/firebase-auth.service';

// @ui
import XervixLoader from './ui/Xervix-loader';
import AuthService from '../services/auth.service';
import { IResidentialUnit } from '../domain/interfaces/Auth';
import { USER_ROLES } from '../utils/ConstantsUtils';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess?: () => void;
  handleCurrentUser?: () => void;
}

interface IParams {
  user: any,
  role: string,
  firebaseToken: string,
  token: string,
  entityId: string,
}

interface IRoleAndEntityId {
  role: string,
  entityId: string,
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onAuthSuccess,
  handleCurrentUser,
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { updateCompanyData } = useOnboarding();
  const { setUserInfo } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAccountExistsModal, setShowAccountExistsModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(() => {
    const showVerification = localStorage.getItem('show_pending_verification') === 'true';
    const userState = localStorage.getItem('user_state');
    return showVerification && userState === 'pending';
  });

  const [allowToSelectUnit, setAllowToSelectUnit] = useState(false);
  const [belongsToUnit, setBelongsToUnit] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [residentialUnits, setResidentialUnits] = useState<IResidentialUnit[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Función para manejar el cambio en los radio buttons
  const handleBelongsToUnitChange = (value: string) => {
    setBelongsToUnit(value);
    if (value === 'no') setSelectedOption('');
  };

  const handleSelectChange = (value: string) => {
    setSelectedOption(value);
  };

  const handleClearSelection = () => {
    setBelongsToUnit('');
    setSelectedOption('');
    setAllowToSelectUnit(false);
    setCurrentUser(null);
  };

  const handleCloseModal = () => {
    handleClearSelection();
    onClose();
    setIsLoading(false);
  };

  // Efecto para limpiar la bandera de verificación pendiente cuando se cierra el modal
  useEffect(() => {
    if (!showPendingModal) localStorage.removeItem('show_pending_verification');

    const userState = localStorage.getItem('user_state');
    if (userState === 'active') {
      setShowPendingModal(false);
      localStorage.removeItem('show_pending_verification');
    }
  }, [showPendingModal]);

  const userExists = () => {
    setShowAccountExistsModal(true);
    handleCloseModal();
    setIsLoading(false);
  };

  const handleUpdateCompanyData = (user: any) => {
    updateCompanyData({
      name: user.displayName?.split(' ')[0] || '',
      lastname: user.displayName?.split(' ').slice(1).join(' ') || '',
      email: user.email || '',
      profileImage: user.photoURL || null
    });
  };

  const userPhotoURL = (user: any) => {
    try {
      localStorage.setItem('userProfileImage', user.photoURL);
      window.dispatchEvent(new CustomEvent('profile-image-updated', {
        detail: { imageUrl: user.photoURL }
      }));
    } catch (error) {
      console.error('Error al manejar la imagen de perfil durante el login:', error);
    }
  };

  const handleBasicUserCreation = (params: IParams) => {
    const { user, firebaseToken, role, entityId } = params;
    const userData: BasicUserCreate = {
      Email: user.email || '',
      Name: user.displayName?.split(' ')[0] || '',
      LastName: user.displayName?.split(' ').slice(1).join(' ') || '',
      ProfilePictureUrl: user.photoURL || null,
      IsActive: true,
      GoogleId: user.uid || '',
      State: 'active',
      Role: role,
      FirebaseToken: firebaseToken,
      unitId: selectedOption || null,
      entityId: entityId || null
    };
    return userData;
  }

  // Guardar la información del usuario en el estado global
  const saveUserInfo = (user: any, token?: string, firebaseToken?: string) => {
    const userInfo: UserInfo = {
      name: user.displayName?.split(' ')[0] || '',
      lastname: user.displayName?.split(' ').slice(1).join(' ') || '',
      email: user.email || '',
      business: 0,
      profileImage: user.photoURL || null,
      token: token || firebaseToken || null
    };
    setUserInfo(userInfo);
    return userInfo;
  }

  // Actualizar el objeto userInfo con la información completa del perfil
  const handleCurrentUserProfile = async (userInfo: UserInfo) => {
    try {
      const userProfile = await UserService.getCurrentUserProfile();
      if (userProfile && setUserInfo && userInfo) {
        const updatedUserInfo: UserInfo = {
          ...userInfo,
          companyId: userProfile.id,
          role: userProfile.role || 'user'
        };
        setUserInfo(updatedUserInfo);
      }
    } catch (profileError) {
      toast.error('Error al obtener perfil del usuario');
    }
  }

  const verifyIfCompanyPartner = async (email: string) => {
    if (!email) return;
    try {
      const response = await AuthService.getCompanyPartnerByEmail(email);
      return response?.partnerId ?? '';
    } catch (error) {
      toast.error('Error al verificar si es empresa partner');
    }
  }

  const verifyIfIsUnit = async (email: string) => {
    if (!email) return;
    try {
      const response = await AuthService.getUnidByEmail(email);
      return response?.unitId ?? '';
    } catch (error) {
      toast.error('Error al verificar si es unidad residencial');
    }
  }

  const verifyIfRealState = async (email: string) => {
    if (!email) return;
    try {
      const response = await AuthService.getRealStateByEmail(email);
      return response?.realStateId ?? '';
    } catch (error) {
      toast.error('Error al verificar si es inmobiliaria');
    }
  }

  const getRole = async (email: string) => {
    let role = USER_ROLES.USER;
    let entityId = '';

    const realStateId = await verifyIfRealState(email);
    if (realStateId) return { role: USER_ROLES.INMOBILIARIA, entityId: realStateId } as IRoleAndEntityId;


    const PartnerId = await verifyIfCompanyPartner(email);
    if (PartnerId) {
      return { role: USER_ROLES.COMPANY_PARTNER, entityId: PartnerId } as IRoleAndEntityId;
    }

    if (!PartnerId) {
      const isUnit = await verifyIfIsUnit(email);
      if (isUnit) {
        return { role: USER_ROLES.USER_ADMIN_UNIT, entityId: isUnit } as IRoleAndEntityId;
      }
    }
    if (belongsToUnit === 'si' && selectedOption) role = USER_ROLES.USER_PARTNER;
    return { role, entityId };
  }

  const handleIsNewUser = async (user: any) => {
    const firebaseToken = localStorage.getItem('firebase_token');
    if (!firebaseToken) return setError('Error al crear tu cuenta. Por favor, intenta nuevamente.');

    const roleAndEntityId = await getRole(user?.email || '');
    const token = localStorage.getItem('auth_token') || firebaseToken;

    const params = {
      user,
      role: roleAndEntityId?.role,
      firebaseToken,
      token,
      entityId: roleAndEntityId?.entityId
    };
    handleSubmitUser(params);
  };

  const handleSubmitUser = async (params: IParams) => {
    const { user, role, firebaseToken, token } = params;
    try {
      const userData = handleBasicUserCreation(params);
      await UserService.createBasicUser(userData);
      await FirebaseAuthService.getTokenForUser();

      const userInfo = saveUserInfo(user, token, firebaseToken);
      if (user.photoURL) localStorage.setItem('userProfileImage', user.photoURL);
      localStorage.setItem('user_role', role);

      handleCurrentUserProfile(userInfo);
      // Emitir evento de autenticación exitosa para actualizar componentes
      window.dispatchEvent(new CustomEvent('auth-success', {
        detail: { user, state: 'active' }
      }));
      handleCloseModal();
      if (onAuthSuccess) onAuthSuccess();
      else navigate('/');
    } catch (error: any) {
      setError('Error al crear tu cuenta. Por favor, intenta nuevamente.');
      await FirebaseAuthService.signOut();
    }
  }

  const isCompanyCreationMode = (isNewUser: boolean) => {
    if (isNewUser) {
      handleCloseModal();
      navigate('/onboarding/category');
      return;
    }
    setShowAccountExistsModal(true);
    setIsLoading(false);
    handleCloseModal();
  }

  // Verificar si el usuario es nuevo y su estado
  const handleFlowCreation = async (user: any) => {
    const isNewUser = localStorage.getItem('is_new_user') === 'true';
    const userState = localStorage.getItem('user_state');
    const hasAuthToken = localStorage.getItem('auth_token') !== null;
    const isCompanyCreation = localStorage.getItem('is_company_creation') === 'true';

    // Si NO es creación de empresa, es un usuario básico
    if (!isCompanyCreation) {
      if (isNewUser) {
        getAllUnits();
        setCurrentUser(user);
        setAllowToSelectUnit(true);
      } else if (userState && hasAuthToken) {
        handleLogin(user, userState);
      } else {
        handleCloseModal();
      }
    } else {
      isCompanyCreationMode(isNewUser);
    }
  }

  const handleLogin = async (user: any, userState: string) => {
    const userInfo: UserInfo = {
      name: user.displayName?.split(' ')[0] || '',
      lastname: user.displayName?.split(' ').slice(1).join(' ') || '',
      email: user.email || '',
      business: 0,
      profileImage: user.photoURL || null,
      token: localStorage.getItem('auth_token')
    };
    setUserInfo(userInfo);
    window.dispatchEvent(new CustomEvent('auth-success', {
      detail: { user, state: userState, userInfo }
    }));
    if (handleCurrentUser) handleCurrentUser();
    handleCloseModal();
    if (onAuthSuccess) onAuthSuccess();
    else navigate('/');
  }

  const handleGoogleSuccess = async (user: any) => {
    setIsLoading(true);
    setError(null);
    try {
      if (user?.accountExists) return userExists();
      handleUpdateCompanyData(user);
      localStorage.removeItem('userProfileImage');
      if (user.photoURL) userPhotoURL(user);
      handleFlowCreation(user);
    } catch (err) {
      setError('Hubo un problema al procesar tu inicio de sesión. Por favor, intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Verificar si es un error de usuario pendiente de verificación
  const handleGoogleError = (error: any) => {
    if (error.message === 'USER_PENDING_VERIFICATION') {
      localStorage.setItem('show_pending_verification', 'true');
      handleCloseModal();
    } else if (localStorage.getItem('show_pending_verification') === 'true' &&
      localStorage.getItem('user_state') === 'pending') {
      handleCloseModal();
    } else {
      setError('Hubo un problema al iniciar sesión. Por favor intenta nuevamente.');
    }
  };

  const handlePreviousStep = () => {
    if (!belongsToUnit) return toast.error('Por favor, selecciona una opción para continuar');
    if (belongsToUnit === 'si' && !selectedOption) return toast.error('Por favor, selecciona una opción para continuar');
    setIsLoading(true);
    setAllowToSelectUnit(false);
    handleIsNewUser(currentUser);
  }

  const getAllUnits = async () => {
    try {
      const units = await AuthService.getAllUnits();
      setResidentialUnits(units);
    } catch (error) {
      toast.error('Error al obtener las unidades residenciales, por favor intenta nuevamente mas tarde');
    }
  }

  if (!isOpen && !showPendingModal && !showAccountExistsModal) return null;

  return (
    <>
      {/* Modal de cuenta ya existente */}
      {showAccountExistsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAccountExistsModal(false);
            }
          }}
        >
          <div className={`bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark text-xervix-text-primary dark:text-xervix-text-primary-dark  rounded-lg max-w-md w-full p-6`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Cuenta Existente</h3>
              <button
                onClick={() => {
                  setShowAccountExistsModal(false);
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>

              <p className={`text-center mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Esta cuenta ya existe y está activa. Para acceder a tu cuenta de empresa, por favor utiliza la opción de inicio de sesión en lugar de crear una nueva cuenta.
              </p>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowAccountExistsModal(false);
                }}
                className="bg-xervix-primary hover:bg-red-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        //onClick={(e) => { handleCloseModal()}}
        >
          <div className={`bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark rounded-lg max-w-[420px] w-full`}>
            {/* Cabecera del modal */}
            <div className="text-center pt-8 pb-4">
              <h1 className="text-4xl font-bold text-xervix-primary mb-4">XERVIX</h1>
              <h2 className={`text-2xl font-semibold mb-2 text-xervix-text-primary dark:text-xervix-text-primary-dark`}>Hola ! Ingresa a tu cuenta</h2>
              <p className={`px-8 pt-2 pb-4 text-xervix-text-primary dark:text-xervix-text-primary-dark`}>
                Accede con Google o Facebook. Sin registros, rápido y seguro.
              </p>
            </div>

            {/* Contenido del modal */}
            <div className="px-12 pb-4">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-4">
                  <div className="w-full flex justify-center items-center">
                    <XervixLoader />
                  </div>
                  <p className={`mt-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Procesando tu inicio de sesión...</p>
                </div>
              ) : (
                <>
                  {error && (
                    <div className={`${theme === 'dark' ? 'bg-red-900 bg-opacity-20 border border-red-500 text-red-300' : 'bg-red-100 border border-red-400 text-red-700'} px-4 py-3 rounded relative mb-4`} role="alert">
                      <span className="block sm:inline">{error}</span>
                    </div>
                  )}

                  {allowToSelectUnit &&
                    <div className="mb-6 p-4 border border-xervix-border dark:border-xervix-border-dark rounded-lg bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark">
                      <h4 className={`text-lg font-medium mb-4 text-xervix-text-primary dark:text-xervix-text-primary-dark`}>
                        ¿Hace parte de una unidad registrada en Xervix?
                      </h4>

                      <div className="space-y-3 mb-4">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="belongsToUnit"
                            value="si"
                            checked={belongsToUnit === 'si'}
                            onChange={(e) => handleBelongsToUnitChange(e.target.value)}
                            className="mr-3 text-xervix-primary focus:ring-xervix-primary"
                          />
                          <span className={`text-xervix-text-primary dark:text-xervix-text-primary-dark`}>
                            Sí
                          </span>
                        </label>

                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="belongsToUnit"
                            value="no"
                            checked={belongsToUnit === 'no'}
                            onChange={(e) => handleBelongsToUnitChange(e.target.value)}
                            className="mr-3 text-xervix-primary focus:ring-xervix-primary"
                          />
                          <span className={`text-xervix-text-primary dark:text-xervix-text-primary-dark`}>
                            No
                          </span>
                        </label>
                      </div>

                      {belongsToUnit === 'si' && (
                        <div className="mt-4">
                          <label className={`block text-sm font-medium mb-2 text-xervix-text-primary dark:text-xervix-text-primary-dark`}>
                            Seleccione una opción:
                          </label>
                          <select
                            value={selectedOption}
                            onChange={(e) => handleSelectChange(e.target.value)}
                            className={`w-full p-3 border border-xervix-border dark:border-xervix-border-dark rounded-md bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark text-xervix-text-primary dark:text-xervix-text-primary-dark focus:ring-2 focus:ring-xervix-secondary focus:border-transparent`}
                          >
                            <option value="">Seleccione una opción</option>
                            {residentialUnits?.map((unit) => (
                              <option key={unit.id} value={unit.id}>{unit.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  }

                  {!allowToSelectUnit &&
                    <>
                      {/* Botón de Google para usuarios básicos */}
                      <GoogleSignInButton
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        className="w-full border border-xervix-border dark:border-xervix-border-dark"
                        text="Continúa con Google"
                        isCompanyCreation={false}
                        setParentLoading={setIsLoading}
                      />

                      {/* Botón de Facebook */}
                      <button
                        className="w-full bg-blue-600 text-white font-medium py-3 px-4 rounded-md flex items-center justify-center mb-4 mt-4 hover:bg-blue-700 transition-colors"
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                        </svg>
                        Continúa con FaceBook
                      </button>
                    </>
                  }

                  {allowToSelectUnit &&
                    <button
                      onClick={handlePreviousStep}
                      className="w-full text-center bg-transparent text-xervix-primary font-medium rounded-md hover:bg-xervix-primary-dark transition-colors cursor-pointer"
                    >
                      Continuar
                    </button>
                  }

                  {/* Separador */}
                  <div className="relative mt-8">
                    <div className="absolute inset-0 flex items-center">
                      <div className={`w-full border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}></div>
                    </div>
                  </div>

                  {/* Sección para empresas */}
                  {!allowToSelectUnit &&
                    <div className="text-center mb-6 mt-12">
                      <h3 className={`text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Crea una cuenta de Empresa</h3>
                      <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        ¿Ofreces servicios? Únete a nuestra comunidad y lleva tu negocio al siguiente nivel.
                      </p>

                      {/* Botón para crear cuenta de empresa - Mantiene la funcionalidad actual */}
                      <div className="">
                        <GoogleSignInButton
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          className="w-full"
                          text="Crear cuenta como Empresa"
                          isCompanyCreation={true}
                        />
                      </div>

                      <div className="text-center mt-4">
                        <button
                          onClick={handleCloseModal}
                          className="w-full bg-white border border-xervix-border dark:border-xervix-border-dark text-xervix-primary font-medium py-3 px-4 rounded-md flex items-center justify-center mb-2 mt-4 hover:bg-gray-300 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  }
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de verificación pendiente */}
      <PendingVerificationModal
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
      />
    </>
  );
};

export default AuthModal;
